import React from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import colors from "common/styles/colors";
import { Typography } from "common/components/Typography";
import BaseChart from "../../components/charts/BaseChart";
import { ChartType } from "../../types/api.types";
import {
  MatchRateBreakdown,
  MatchRateGroupBy,
} from "../../types/matchRate.types";

interface MatchRateBreakdownChartProps {
  breakdown?: MatchRateBreakdown;
  groupBy: MatchRateGroupBy;
  onGroupByChange: (groupBy: MatchRateGroupBy) => void;
}

const toPercent = (rate: number | null) => (rate == null ? 0 : rate * 100);

export const MatchRateBreakdownChart: React.FC<
  MatchRateBreakdownChartProps
> = ({ breakdown, groupBy, onGroupByChange }) => {
  const { t } = useTranslation();
  const buckets = breakdown?.buckets ?? [];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "12px",
        }}
      >
        <Typography variant="h2" textStyle={{ fontSize: 18 }}>
          {t("matchRate.chartTitle")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={groupBy}
          onChange={(_event, value: MatchRateGroupBy | null) => {
            if (value) {
              onGroupByChange(value);
            }
          }}
        >
          <ToggleButton value="month">{t("matchRate.groupBy.month")}</ToggleButton>
          <ToggleButton value="quarter">
            {t("matchRate.groupBy.quarter")}
          </ToggleButton>
          <ToggleButton value="year">{t("matchRate.groupBy.year")}</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <BaseChart
        chartType={ChartType.Lines}
        curve="linear"
        max={100}
        height={360}
        data={buckets.map((bucket) => ({
          x: bucket.period,
          y: toPercent(bucket.combined.listing_match_rate),
        }))}
        chartLabel={t("matchRate.listingMatch")}
        color={colors.purple2}
        yAxisLabel={t("matchRate.chartPercent")}
        extraSeries={[
          {
            data: buckets.map((bucket) => ({
              x: bucket.period,
              y: toPercent(bucket.combined.project_match_rate),
            })),
            label: t("matchRate.projectMatch"),
            color: colors.green,
            curve: "linear",
          },
          {
            data: buckets.map((bucket) => ({
              x: bucket.period,
              y: toPercent(bucket.combined.any_match_rate),
            })),
            label: t("matchRate.anyMatch"),
            color: colors.purple3,
            curve: "linear",
          },
        ]}
      />
      {!!breakdown?.missing_sold_date && (
        <Typography
          variant="caption"
          textStyle={{ color: colors.grey, marginTop: 8 }}
        >
          {t("matchRate.missingSoldDate", {
            count: breakdown.missing_sold_date,
          })}
        </Typography>
      )}
    </div>
  );
};
